import { useCallback, useEffect, useRef } from 'react';

/** Travel at which the gesture picks its axis - once, and for good. */
const DECIDE_PX = 10;
/** Released past this, it is a reply. WhatsApp sits at about this distance. */
const TRIGGER_PX = 56;
/** Pull past the trigger comes at a fraction - the "there is no more" feel. */
const OVERPULL_RATE = 0.35;
const SETTLE_MS = 250;

/**
 * Swipe a message to reply to it - WhatsApp's gesture, drag right and let go.
 *
 * Touch and pen only, like the long-press: a mouse has the hover gutter and no
 * business dragging rows. The hold sheet stays for edit and delete; this is the
 * fast path for the one action a conversation is made of.
 *
 * Everything is painted by hand on the two refs rather than through state - a
 * drag is a style update per pointer move, and a render per move is how a
 * gesture ends up trailing the finger.
 *
 * The heart of it is the messengers' axis lock, because a thumb never travels
 * straight. The first ~10px of movement decide the gesture ONCE: mostly
 * vertical and it is a scroll this hook never touches; mostly rightward and it
 * is a swipe - after which two things hold until the finger lifts:
 *
 *   - **The thread stops scrolling.** A native non-passive touchmove listener
 *     calls preventDefault while engaged - the row's own `touch-action: pan-y`
 *     cannot do this, it only DECLARES the vertical pan allowed, so a diagonal
 *     thumb kept scrolling the chat under the sliding bubble and iOS would
 *     eventually claim the gesture and cancel the pointer mid-swipe. It has to
 *     be a native listener: React registers its touch handlers passively, so
 *     its own onTouchMove is not allowed to prevent anything. And it has to be
 *     this way around - `touch-action: none` on the row would kill scrolling
 *     over every message, which is the whole surface of the thread.
 *   - **Vertical drift stops mattering.** An engaged swipe used to be killed
 *     by 24px of drift, which read as the gesture randomly braking mid-pull.
 *     The decision was made at the start; noise after it is just noise.
 *
 * The rest of what makes or breaks it:
 *
 *   - **Scrubbers keep their drag.** Both players seek by horizontal drag and
 *     mark their track `role="slider"` - a swipe starting there is a seek.
 *   - **The click after the swipe is not a click.** Released over a photo, the
 *     browser still synthesises one, and it would open the lightbox.
 */
export const useSwipeToReply = (onReply?: () => void) => {
  /** The element that slides - the bubble column, footer and all. */
  const trackRef = useRef<HTMLDivElement | null>(null);
  /** The reply disc hanging off the bubble. It rides the track, so the drag
      only fades and grows it - the movement comes free. */
  const hintRef = useRef<HTMLSpanElement | null>(null);

  const originRef = useRef<{ x: number; y: number; id: number } | null>(null);
  const engagedRef = useRef(false);
  /** Set when the axis decision went to the scroll (or the wrong direction). */
  const deadRef = useRef(false);
  /** Set past the trigger: releasing now replies. */
  const armedRef = useRef(false);
  /** Set when a swipe happened, so the click it turns into can be swallowed. */
  const firedRef = useRef(false);
  const willChangeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The scroll lock. On the document for the duration of one touch rather than
   * on every row for the lifetime of the thread - one listener at a time
   * instead of hundreds, and only while a finger is actually down.
   */
  const blockScroll = useCallback((event: TouchEvent) => {
    if (engagedRef.current && event.cancelable) event.preventDefault();
  }, []);

  const unlisten = useCallback(() => {
    document.removeEventListener('touchmove', blockScroll);
  }, [blockScroll]);

  // A row unmounted mid-gesture (a socket refetch) must not leave the lock on.
  useEffect(() => unlisten, [unlisten]);

  const paint = useCallback((pull: number, settle: boolean) => {
    const track = trackRef.current;
    const hint = hintRef.current;
    const transition = settle
      ? `transform ${SETTLE_MS}ms ease, opacity ${SETTLE_MS}ms ease`
      : 'none';

    const eased =
      pull <= TRIGGER_PX
        ? pull
        : TRIGGER_PX + (pull - TRIGGER_PX) * OVERPULL_RATE;
    const progress = Math.min(1, pull / TRIGGER_PX);

    if (track) {
      track.style.transition = transition;
      track.style.transform = eased ? `translateX(${eased}px)` : '';
    }
    if (hint) {
      hint.style.transition = transition;
      hint.style.opacity = String(progress);
      hint.style.transform = `scale(${0.6 + 0.4 * progress})`;
    }
  }, []);

  const reset = useCallback(
    (settle: boolean) => {
      originRef.current = null;
      engagedRef.current = false;
      deadRef.current = false;
      armedRef.current = false;
      unlisten();
      paint(0, settle);

      /* The layer promotion is only for the drag; released after the settle
         animation has used it, so hundreds of resting rows are not all kept
         on their own compositor layers. */
      if (willChangeTimer.current) clearTimeout(willChangeTimer.current);
      willChangeTimer.current = setTimeout(() => {
        if (trackRef.current) trackRef.current.style.willChange = '';
      }, SETTLE_MS);
    },
    [paint, unlisten]
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!onReply) return;
      if (event.pointerType === 'mouse') return;

      /* A drag that starts on something with its own drag or tap is theirs:
         the seek tracks, a link, a button. */
      const target = event.target as HTMLElement | null;
      if (
        target?.closest('[role="slider"], a, button, input, textarea, video')
      ) {
        return;
      }

      firedRef.current = false;
      deadRef.current = false;
      originRef.current = {
        x: event.clientX,
        y: event.clientY,
        id: event.pointerId,
      };
      /* Armed before the axis is decided - by the time it is, the browser is
         already mid-touch and a listener added then would miss the very moves
         that need cancelling. It no-ops until the gesture engages. */
      document.addEventListener('touchmove', blockScroll, { passive: false });
    },
    [onReply, blockScroll]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const origin = originRef.current;
      if (!origin || origin.id !== event.pointerId || deadRef.current) return;

      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;

      if (!engagedRef.current) {
        // The axis decision: one look, at the first decisive bit of travel.
        if (Math.hypot(dx, dy) < DECIDE_PX) return;
        if (Math.abs(dy) >= Math.abs(dx) || dx <= 0) {
          deadRef.current = true;
          unlisten();
          return;
        }
        engagedRef.current = true;
        if (trackRef.current) trackRef.current.style.willChange = 'transform';
      }

      const pull = Math.max(0, dx);

      if (!armedRef.current && pull >= TRIGGER_PX) {
        armedRef.current = true;
        // A tick under the finger at the point of no return, where supported.
        navigator.vibrate?.(15);
      } else if (armedRef.current && pull < TRIGGER_PX) {
        armedRef.current = false;
      }

      paint(pull, false);
    },
    [paint, unlisten]
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent) => {
      const origin = originRef.current;
      if (!origin || origin.id !== event.pointerId) return;

      const reply = armedRef.current;
      if (engagedRef.current) firedRef.current = true;
      reset(true);
      if (reply) onReply?.();
    },
    [onReply, reset]
  );

  const onPointerCancel = useCallback(() => reset(true), [reset]);

  /** The synthesised click after a swipe opens nothing. */
  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (!firedRef.current) return;
    firedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    trackRef,
    hintRef,
    rowHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onClickCapture,
    },
  };
};
